'use client'

import React, { useEffect, useState } from 'react'

import ColoredText from '../../text/ColoredText'
import { FontModeValues } from '../../modes/FontMode'
import StandardButton from '../../buttons/StandardButton'
import { ButtonModeValues } from '../../modes/ButtonMode'
import { Rating } from '@mui/material'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import StarIcon from '@mui/icons-material/Star'
import StandardModal from '../../modal/StandardModal'
import type { IAgoraRTCRemoteUser } from 'agora-rtc-react'
import type { RtmTextMessage, RtmClient, RtmChannel } from 'agora-rtm-react'
import { receiveMessage, sendMessage } from './MessageHelperFunctions'
import {
  type GetRuntimeInterviewTemplateDto,
  VerificationRequestDtoTypeEnum,
  type GetTemplateSection,
  type GetQuestionDto,
  InterviewRuntimeTemplateControllerApi
} from '../../../api/ic'
import { showErrorToast, showSuccessToast } from '../../helper'
import type { GetQuestionWithAnswerDto } from '../../../api/ic/models/GetQuestionWithAnswerDto'

interface TemplateProps {
  template: GetRuntimeInterviewTemplateDto
  setTemplate: React.Dispatch<React.SetStateAction<GetRuntimeInterviewTemplateDto | undefined>>
  client: RtmClient | null
  session: RtmChannel | null
  dontAskAgain?: boolean
  lastAnswerQuestion?: string
  type: VerificationRequestDtoTypeEnum
  subscriber?: IAgoraRTCRemoteUser | null
  interviewCompleted: boolean
  setInterviewCompleted: React.Dispatch<React.SetStateAction<boolean>>
  isTaker?: boolean // New prop to determine if user is the interview taker
}

function InterviewQuestionsTemplate (props: TemplateProps) {
  const [currentSection, setCurrentSection] = useState<GetTemplateSection | undefined>(undefined)
  const [currentQuestion, setCurrentQuestion] = useState<GetQuestionDto | undefined>(undefined)
  const [modalOpenedReason, setModalOpenedReason] = useState<
  'ASK_FOR_NEXT' | 'REVIEW_ERROR' | 'SWITCH_WITHOUT_RATING' | undefined
  >(undefined)
  const [modalContent, setModalContent] = useState<string>('')
  const interviewQuestionApi = new InterviewRuntimeTemplateControllerApi()
  const [review, setReview] = useState<number | undefined>(undefined)
  const [anim, setAnim] = useState(false)
  const [dontAskAgain, setDontAskAgain] = useState(props.template.dontAskAgain)
  const [open, setOpen] = useState(false)
  const [receivedContent, setReceivedContent] = useState<string | undefined>(undefined)
  const [nextButtonLoading, setNextButtonLoading] = useState(false)
  const [showQuestionSelector, setShowQuestionSelector] = useState(false)
  const [answeredQuestions, setAnsweredQuestions] = useState<GetQuestionWithAnswerDto[]>(props.template.answers || [])

  // Determine if user is interview taker (can control questions)
  const isTaker =
      props.isTaker ??
      (props.type === VerificationRequestDtoTypeEnum.RoleTaker ||
          props.type === VerificationRequestDtoTypeEnum.RoleCorporateTaker)

  // Session event listener
  useEffect(() => {
    if (props.session) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      props.session.on('ChannelMessage', (event: RtmTextMessage) => {
        const message = receiveMessage(event.text)
        if (message?.type === 'nextQuestion') {
          const parts = message.message.data.split('&&&&&')
          const questionId = parts[0].split('&&=&&')[1]
          const content = parts[1].split('&&=&&')[1]

          setReceivedContent(content)
          props.setTemplate({ ...props.template, currentQuestionId: questionId })
          // Reset review when question changes
          setReview(0)
        }
        if (message?.type === 'interviewFinished') {
          props.setInterviewCompleted(true)
        }
      })
    }
  }, [props.session])

  // Find current section and question based on template
  useEffect(() => {
    let found = false
    for (const section of props.template.sections) {
      for (const eachQuestion of section.questions) {
        if (eachQuestion.id === props.template.currentQuestionId) {
          if (receivedContent) {
            eachQuestion.content = receivedContent
          }
          setCurrentQuestion(eachQuestion)
          setCurrentSection(section)
          found = true
          break
        }
      }
      if (found) {
        break
      }
    }
    setAnim(false)
    setTimeout(() => {
      setAnim(true)
    }, 300)

    // Initialize answered questions from template if available
    if (props.template.answers && props.template.answers.length > 0) {
      setAnsweredQuestions(props.template.answers)
    }

    // Reset review when question changes
    setReview(0)
  }, [props.template, receivedContent])

  // Fire next event
  const fireNextEvent = (questionDto: GetQuestionDto) => {
    if (props.session) {
      const data = `questionId&&=&&${questionDto.id}&&&&&content&&=&&${questionDto.content}`
      void sendMessage(props.session, { type: 'nextQuestion', message: { data } })
      const parts = data.split('&&&&&')
      const questionId = parts[0].split('&&=&&')[1]
      const content = parts[1].split('&&=&&')[1]

      setReceivedContent(content)
      props.setTemplate({ ...props.template, currentQuestionId: questionId })
      // Reset review when question changes
      setReview(0)
    }
  }

  // Fire finished event
  const fireFinishedEvent = () => {
    if (props.session) {
      void sendMessage(props.session, { type: 'interviewFinished', message: {} })
      props.setInterviewCompleted(true)
    }
  }

  // Find the next unanswered question in the template
  const findNextUnansweredQuestion = (): GetQuestionDto | null => {
    if (!currentQuestion) return null

    // Flatten all questions from all sections
    const allQuestions = props.template.sections.flatMap((section) => section.questions)

    // Find the index of the current question
    const currentIndex = allQuestions.findIndex((q) => q.id === currentQuestion.id)

    // Look for the next unanswered question after the current one
    for (let i = currentIndex + 1; i < allQuestions.length; i++) {
      const question = allQuestions[i]
      // Skip questions that are already answered
      if (!answeredQuestions.some((aq) => aq.id === question.id)) {
        return question
      }
    }

    // If all questions after current are answered, return the first unanswered question
    for (let i = 0; i < currentIndex; i++) {
      const question = allQuestions[i]
      if (!answeredQuestions.some((aq) => aq.id === question.id)) {
        return question
      }
    }

    // If all questions are answered, return the next question in sequence
    if (currentIndex < allQuestions.length - 1) {
      return allQuestions[currentIndex + 1]
    }

    return null
  }

  // Handle don't ask again
  const handleDontAskAgain = async () => {
    if (dontAskAgain !== props.template.dontAskAgain) {
      try {
        await interviewQuestionApi.dontAskForNext({
          interviewId: props.template.interviewId
        })
        setDontAskAgain(true)
        props.template.dontAskAgain = true
      } catch (error) {
        showErrorToast('Could not update')
      }
    }
    await Promise.resolve()
  }

  // Check if this is the last question or if all other questions are rated
  const isLastOrAllOthersRated = () => {
    // Count total questions
    const allQuestions = props.template.sections.flatMap((section) => section.questions)

    // Get all questions that require review
    const questionsRequiringReview = allQuestions.filter((q) => q.requiresReview)

    // Check if this is the last question
    const isLastQuestion = allQuestions.findIndex((q) => q.id === currentQuestion?.id) === allQuestions.length - 1

    // Check if all questions that require review are answered
    const allRequiredQuestionsAnswered = questionsRequiringReview.every((q) => {
      // If it's the current question and it has a review, or it's in the answered questions list
      return (q.id === currentQuestion?.id && review !== undefined) || answeredQuestions.some((aq) => aq.id === q.id)
    })

    return isLastQuestion || allRequiredQuestionsAnswered
  }

  // Answer the current question and proceed
  const answerAndProceed = async () => {
    if (!currentQuestion) {
      setNextButtonLoading(false)
      return
    }

    try {
      // Always call answer API for the current question
      const answerResponse = await interviewQuestionApi.answer({
        interviewId: props.template.interviewId,
        questionId: currentQuestion.id,
        interviewQuestionAnswerRequestDto: {
          points: review
        }
      })

      console.log('answer response:', answerResponse)

      // Update the local answered questions state with the response data
      if (answerResponse) {
        // The response contains the answered question data
        const answeredQuestion = answerResponse as unknown as GetQuestionWithAnswerDto

        // Update or add the answer to local state
        setAnsweredQuestions((prev) => {
          const existingIndex = prev.findIndex((q) => q.id === answeredQuestion.id)
          if (existingIndex >= 0) {
            // Update existing answer
            const updated = [...prev]
            updated[existingIndex] = answeredQuestion
            return updated
          } else {
            // Add new answer
            return [...prev, answeredQuestion]
          }
        })
      }

      // Check if we should finish the interview
      if (isLastOrAllOthersRated()) {
        try {
          const finishResponse = await interviewQuestionApi.finish({
            interviewId: props.template.interviewId
          })

          if (finishResponse) {
            showSuccessToast('Interview Finished Successfully')
            fireFinishedEvent()
          }
        } catch (error) {
          showErrorToast('Could not finish the interview')
          console.error(error)
        }
      } else {
        // Find the next unanswered question in the template
        const nextQuestion = findNextUnansweredQuestion()

        if (nextQuestion) {
          // Fire next event with the found next question
          fireNextEvent(nextQuestion)
        } else {
          // If no next question found, finish the interview
          try {
            const finishResponse = await interviewQuestionApi.finish({
              interviewId: props.template.interviewId
            })

            if (finishResponse) {
              showSuccessToast('Interview Finished Successfully')
              fireFinishedEvent()
            }
          } catch (error) {
            showErrorToast('Could not finish the interview')
            console.error(error)
          }
        }
      }

      // Reset review after answering
      setReview(0)
      setOpen(false)
    } catch (error) {
      console.error('Error answering question:', error)
      showErrorToast('Could not answer the question')
    } finally {
      setNextButtonLoading(false)
    }
  }

  // Check for modal and next
  const checkForModalAndNext = async () => {
    setNextButtonLoading(true)
    // In case the review is required, open modal
    if (currentQuestion?.requiresReview && !review) {
      setOpen(true)
      setModalOpenedReason('REVIEW_ERROR')
      setModalContent('You need to review answer for this question!')
      setNextButtonLoading(false)
      return
    }

    // In case the state is AskAgain, open modal
    if (!dontAskAgain) {
      setOpen(true)
      setModalOpenedReason('ASK_FOR_NEXT')
      setModalContent('Once you go forward, there is no way back, be aware!')
      setNextButtonLoading(false)
      return
    }

    await handleDontAskAgain()
    await answerAndProceed()
  }

  // Handle opening question selector
  const handleOpenQuestionSelector = () => {
    // Check if rating is required for current question
    if (currentQuestion?.requiresReview && !review) {
      setOpen(true)
      setModalOpenedReason('SWITCH_WITHOUT_RATING')
      setModalContent('This question requires a rating. Do you want to leave without rating?')
      return
    }

    // If no rating required or already rated, open selector
    setShowQuestionSelector(true)
  }

  // Handle question selection
  const handleQuestionSelect = (question: GetQuestionDto) => {
    // Fire next event for the selected question
    fireNextEvent(question)
    setReview(0)
    setShowQuestionSelector(false)
    void interviewQuestionApi
      .switchQuestion({
        interviewId: props.template.interviewId,
        questionId: question.id
      })
      .then((res: any) => {
        console.log('switch', res)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  // Handle switch without rating
  const handleSwitchWithoutRating = () => {
    setOpen(false)
    setShowQuestionSelector(true)
  }

  // Check if a question has been answered
  const isQuestionAnswered = (questionId: string) => {
    return answeredQuestions.some((q) => q.id === questionId)
  }

  // Get the rating for an answered question
  const getQuestionRating = (questionId: string) => {
    const answeredQuestion = answeredQuestions.find((q) => q.id === questionId)
    return answeredQuestion ? answeredQuestion.points : undefined
  }

  return (
    <div
      className="d-flex flex-column justify-content-between user-select-none"
      style={{ width: '100%', margin: '0 auto', minHeight: '30vh', height: '30vh' }}
      >
      <div className="d-flex justify-content-start">
        <div className="ms-5 me-2 d-flex flex-row justify-content-start user-select-none" style={{ width: '20%' }}>
          <h3 className="text-white">{currentSection?.label}</h3>
        </div>

        <div className="flex-1" style={{ width: '80%' }}>
          <p
            className="text-white mb-3"
            style={{
              fontSize: '18px',
              maxHeight: '160px',
              minHeight: '160px',
              overflowY: 'scroll',
              userSelect: 'none'
            }}
            >
            {currentQuestion?.content}
          </p>

          {isTaker && (
            <>
              {currentQuestion?.requiresReview && (
              <div
                className="d-flex align-items-center mb-4"
                style={{
                  display: anim ? 'flex' : 'none'
                }}
                      >
                <Rating
                  name="simple-controlled"
                  value={review || 0}
                  max={5}
                  size="medium"
                  onChange={(event, newValue) => {
                    setReview(newValue ?? undefined)
                  }}
                  emptyIcon={<StarOutlineIcon style={{ color: 'white' }} fontSize="inherit" />}
                  icon={<StarIcon style={{ color: 'white' }} fontSize="inherit" />}
                        />
                <span className="ms-2 text-white user-select-none">{`${review ?? '0'}/5`}</span>
              </div>
              )}

              <div className="d-flex justify-content-between gap-5">
                <button
                  onClick={handleOpenQuestionSelector}
                  className="btn text-white"
                  style={{
                    border: '1px solid white',
                    borderRadius: '30px',
                    padding: '6px 12px',
                    background: 'transparent'
                  }}
                    >
                  Switch question
                </button>

                <button
                  onClick={() => {
                    void checkForModalAndNext()
                  }}
                  className="btn text-white"
                  style={{
                    border: '1px solid white',
                    borderRadius: '30px',
                    padding: '6px 12px',
                    background: 'transparent',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  disabled={nextButtonLoading}
                    >
                  {isLastOrAllOthersRated() ? 'Finish' : 'Next'}
                  {!isLastOrAllOthersRated() && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="ms-2"
                    viewBox="0 0 16 16"
                          >
                    <path
                      fillRule="evenodd"
                      d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                            />
                  </svg>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Question Selector Modal */}
      <StandardModal
        isOpen={showQuestionSelector}
        closeWhenClickingOutOfModal={true}
        title="Select Question"
        width={`${(screen.availWidth * 60) / 100}px`}
        titleColor="black"
        titleFontSize={FontModeValues.LARGE}
        needDividers={false}
        closeModal={setShowQuestionSelector}
        footerComponent={
          <div className="w-100 d-flex justify-content-end">
            <StandardButton
              text="Close"
              backgroundColor="#2E302F"
              sizeMode={ButtonModeValues.TINY_SMALL}
              onClick={() => {
                setShowQuestionSelector(false)
              }}
                />
          </div>
            }
        >
        <div className="pt-3" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <div className="d-flex flex-column gap-3">
            {props.template.sections.flatMap((section) =>
              section.questions.map((question) => {
                const isCurrentQuestion = question.id === currentQuestion?.id
                const isAnswered = isQuestionAnswered(question.id)
                const questionRating = getQuestionRating(question.id)

                return (
                  <div
                    key={question.id}
                    style={{
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: isCurrentQuestion ? '#0088cc' : isAnswered ? '#4caf50' : '#dee2e6',
                      backgroundColor: isCurrentQuestion
                        ? 'rgba(0, 136, 204, 0.1)'
                        : isAnswered
                          ? 'rgba(76, 175, 80, 0.05)'
                          : 'white'
                    }}
                        >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-1">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <ColoredText fontWeight={500} singleText={`Question ${question.order}`} />
                          <span className="px-2 py-0.5 bg-light rounded">
                            <ColoredText singleText={section.label} />
                          </span>
                          {isCurrentQuestion && (
                            <span className="px-2 py-0.5 text-white rounded" style={{ backgroundColor: '#8e44ad' }}>
                              <ColoredText color="white" singleText="Current" />
                            </span>
                          )}
                          {isAnswered && !isCurrentQuestion && (
                            <span className="px-2 py-0.5 text-white rounded" style={{ backgroundColor: '#4caf50' }}>
                              <ColoredText color="white" singleText="Answered" />
                            </span>
                          )}
                        </div>
                        <ColoredText color="black" singleText={question.content} />

                        {/* Show rating for answered questions */}
                        {isAnswered && questionRating !== undefined && question.requiresReview && (
                          <div className="mt-2 d-flex align-items-center">
                            <ColoredText color="#555" singleText="Rating:" />
                            <span style={{ color: '#f39c12', marginLeft: '8px' }}>
                              {Array(5)
                                .fill(0)
                                .map((_, i) => (
                                  <span key={i}>{i < questionRating ? '★' : '☆'}</span>
                                ))}
                              <span style={{ marginLeft: '4px' }}>
                                (
                                {questionRating}
                                /5)
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <StandardButton
                          backgroundColor="#2E302F"
                          sizeMode={ButtonModeValues.CUSTOM_FONT}
                          text="Select"
                          onClick={() => {
                            handleQuestionSelect(question)
                          }}
                              />
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </StandardModal>

      {/* Warning/Review Modal */}
      <StandardModal
        isOpen={open}
        closeWhenClickingOutOfModal={false}
        title={modalOpenedReason === 'SWITCH_WITHOUT_RATING' ? 'Rating Required' : 'Warning!'}
        titleColor="black"
        titleFontSize={FontModeValues.LARGE}
        needDividers={false}
        closeModal={setOpen}
        footerComponent={
          <div className="w-100">
            {modalOpenedReason === 'REVIEW_ERROR' ? (
            // Simple review required modal for Next button
              <div className="d-flex flex-row justify-content-end w-100">
                <StandardButton
                  text="Okay"
                  backgroundColor="#2E302F"
                  sizeMode={ButtonModeValues.TINY_SMALL}
                  onClick={() => {
                    setOpen(false)
                  }}
                      />
              </div>
            ) : modalOpenedReason === 'SWITCH_WITHOUT_RATING' ? (
            // Modal for switching without rating
              <div className="d-flex flex-row justify-content-end gap-2">
                <StandardButton
                  text="Stay and Rate"
                  backgroundColor="#2E302F"
                  sizeMode={ButtonModeValues.TINY_SMALL}
                  onClick={() => {
                    setOpen(false)
                  }}
                      />
                <StandardButton
                  text="I'll Rate Later"
                  backgroundColor="rgba(46,48,47,0.71)"
                  sizeMode={ButtonModeValues.TINY_SMALL}
                  onClick={handleSwitchWithoutRating}
                      />
              </div>
            ) : (
            // Warning modal for Next button
              <div className="d-flex flex-row justify-content-between align-items-center">
                <div
                  className="d-flex flex-row justify-content-center align-items-stretch gap-2"
                  onClick={() => {
                    setDontAskAgain(!dontAskAgain)
                  }}
                      >
                  <input style={{ accentColor: '#2E302F' }} type="checkbox"
                    checked={dontAskAgain} />
                  <ColoredText cursor="pointer" singleText="Don't ask again"
                    userSelect={'none'} lineHeight={1} />
                </div>
                <div className="d-flex flex-row justify-content-center gap-2">
                  <StandardButton
                    text="Wait"
                    backgroundColor="#2E302F"
                    sizeMode={ButtonModeValues.TINY_SMALL}
                    onClick={() => {
                      setDontAskAgain(false)
                      setOpen(false)
                    }}
                        />
                  <StandardButton
                    text="Next"
                    backgroundColor="rgba(46,48,47,0.71)"
                    sizeMode={ButtonModeValues.TINY_SMALL}
                    onClick={() => {
                      void handleDontAskAgain().then(() => {
                        void answerAndProceed()
                      })
                    }}
                        />
                </div>
              </div>
            )}
          </div>
            }
        >
        <div className="pt-3">
          <ColoredText centered={true} lineHeight={1}
            singleText={modalContent} />
        </div>
      </StandardModal>
    </div>
  )
}

export default InterviewQuestionsTemplate
